import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import { Briefcase, MapPin, X, ArrowLeft, Clock, GraduationCap, Users, IndianRupee, BookOpen, CheckCircle } from 'lucide-react';
import apiClient from '../../services/apiClient';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '300px', borderRadius: '12px' };
const defaultCenter = { lat: 28.6139, lng: 77.2090 };
const libraries = ['places'];

export const CreateJob = ({ editMode = false }) => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingJob, setLoadingJob] = useState(editMode);
  const [success, setSuccess] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectInput, setSubjectInput] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    type: 'full_time',
    location: '',
    salary: '',
    experience: '',
    description: '',
    organization: '',
    latitude: null,
    longitude: null,
    number_of_positions: 1,
    qualifications: '',
    class_level: '',
    board: '',
    class_mode: 'offline',
    timing: '',
    benefits: '',
    urgency: 'normal'
  });

  // Fetch existing job data in edit mode
  useEffect(() => {
    if (editMode && jobId) {
      fetchJobData();
    }
  }, [editMode, jobId]);

  const fetchJobData = async () => {
    try {
      const job = await apiClient.get(`/jobs/${jobId}`);
      setFormData({
        title: job.title || '',
        type: job.type || 'full_time',
        location: job.location || '',
        salary: job.salary || '',
        experience: job.experience || '',
        description: job.description || '',
        organization: job.organization || '',
        latitude: job.latitude || null,
        longitude: job.longitude || null,
        number_of_positions: job.number_of_positions || 1,
        qualifications: job.qualifications || '',
        class_level: job.class_level || '',
        board: job.board || '',
        class_mode: job.class_mode || 'offline',
        timing: job.timing || '',
        benefits: job.benefits || '',
        urgency: job.urgency || 'normal'
      });
      setSubjects(job.subjects || []);
      if (job.latitude && job.longitude) {
        setMapCenter({ lat: job.latitude, lng: job.longitude });
        setMarkerPosition({ lat: job.latitude, lng: job.longitude });
      }
    } catch (error) {
      toast.error('Failed to load job data');
      navigate('/dashboard');
    } finally {
      setLoadingJob(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addSubject = () => {
    if (subjectInput.trim() && !subjects.includes(subjectInput.trim())) {
      setSubjects([...subjects, subjectInput.trim()]);
      setSubjectInput('');
    }
  };

  const removeSubject = (subject) => {
    setSubjects(subjects.filter(s => s !== subject));
  };

  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setFormData(prev => ({
          ...prev,
          location: results[0].formatted_address
        }));
      }
    });
  }, []);

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setFormData(prev => ({
          ...prev,
          location: place.formatted_address || '',
          latitude: lat,
          longitude: lng
        }));
      }
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
          toast.success('Location captured!');
        },
        () => toast.error('Unable to fetch location')
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (subjects.length === 0) {
      toast.error('Please add at least one subject');
      return;
    }

    setLoading(true);
    try {
      const jobData = {
        ...formData,
        subjects,
        number_of_positions: parseInt(formData.number_of_positions) || 1,
      };

      if (editMode && jobId) {
        await apiClient.put(`/jobs/${jobId}`, jobData);
        toast.success('Job updated successfully!');
      } else {
        jobData.organization_id = user.id;
        jobData.review_status = 'pending';
        jobData.unlock_price = 0;
        await apiClient.post('/jobs/create', jobData);
        toast.success('Job posted successfully! Awaiting admin review.');
      }

      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'post'} job`);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'organization') {
    navigate('/dashboard');
    return null;
  }

  if (loadingJob) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job data...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md text-center p-8">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Job {editMode ? 'Updated' : 'Posted'} Successfully!
          </h2>
          <p className="text-gray-600">Redirecting to dashboard...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {editMode ? 'Edit Job Posting' : 'Post a New Job'}
          </h1>
          <p className="text-gray-600">
            {editMode ? 'Update the details of your job posting' : 'Fill in the details to find the perfect tutor for your organization'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Job Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-600" />
                Job Details
              </CardTitle>
              <CardDescription>Provide the basic information about the position</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label htmlFor="title">Job Title *</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="e.g., Mathematics Teacher for Grade 10"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="job-title-input"
                />
              </div>

              <div>
                <Label htmlFor="organization">Organization Name *</Label>
                <Input
                  id="organization"
                  name="organization"
                  placeholder="Your organization name"
                  value={formData.organization}
                  onChange={handleChange}
                  required
                  data-testid="job-org-input"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Job Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger data-testid="job-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="freelance">Freelance</SelectItem>
                      <SelectItem value="visiting">Visiting Faculty</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Urgency</Label>
                  <Select value={formData.urgency} onValueChange={(value) => setFormData({ ...formData, urgency: value })}>
                    <SelectTrigger data-testid="job-urgency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediate Hiring</SelectItem>
                      <SelectItem value="urgent">Urgent (within 1 week)</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="flexible">Flexible Timeline</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="number_of_positions">No. of Positions</Label>
                  <Input
                    id="number_of_positions"
                    name="number_of_positions"
                    type="number"
                    min="1"
                    placeholder="1"
                    value={formData.number_of_positions}
                    onChange={handleChange}
                    data-testid="job-positions-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Job Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe the role, responsibilities, expectations, and what makes this position special..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  required
                  data-testid="job-description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Teaching Requirements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-purple-600" />
                Teaching Requirements
              </CardTitle>
              <CardDescription>Specify what you're looking for in a candidate</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Subjects */}
              <div>
                <Label>Subjects to Teach *</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={subjectInput}
                    onChange={(e) => setSubjectInput(e.target.value)}
                    placeholder="Type a subject and press Add"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                    data-testid="subject-input"
                  />
                  <Button type="button" onClick={addSubject} data-testid="add-subject-btn">Add</Button>
                </div>
                {subjects.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {subjects.map((subject, idx) => (
                      <Badge key={idx} variant="secondary" className="px-3 py-1.5 text-sm">
                        {subject}
                        <button type="button" onClick={() => removeSubject(subject)} className="ml-2 hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="class_level">Class / Grade Level</Label>
                  <Input
                    id="class_level"
                    name="class_level"
                    placeholder="e.g., Class 9-12, College, Competitive Exams"
                    value={formData.class_level}
                    onChange={handleChange}
                    data-testid="class-level-input"
                  />
                </div>

                <div>
                  <Label>Board / Curriculum</Label>
                  <Select value={formData.board || 'any'} onValueChange={(value) => setFormData({ ...formData, board: value === 'any' ? '' : value })}>
                    <SelectTrigger data-testid="board-select">
                      <SelectValue placeholder="Select board" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any / Not Applicable</SelectItem>
                      <SelectItem value="cbse">CBSE</SelectItem>
                      <SelectItem value="icse">ICSE</SelectItem>
                      <SelectItem value="state_board">State Board</SelectItem>
                      <SelectItem value="ib">International Baccalaureate (IB)</SelectItem>
                      <SelectItem value="igcse">IGCSE</SelectItem>
                      <SelectItem value="competitive">Competitive Exams (JEE/NEET)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Class Mode</Label>
                  <Select value={formData.class_mode} onValueChange={(value) => setFormData({ ...formData, class_mode: value })}>
                    <SelectTrigger data-testid="class-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="offline">Offline (At Centre)</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Online + Offline)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="timing">Timing / Schedule</Label>
                  <Input
                    id="timing"
                    name="timing"
                    placeholder="e.g., Mon-Fri 9AM-3PM, Weekends only"
                    value={formData.timing}
                    onChange={handleChange}
                    data-testid="timing-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="experience">Experience Required</Label>
                  <Select value={formData.experience || 'any'} onValueChange={(value) => setFormData({ ...formData, experience: value === 'any' ? '' : value })}>
                    <SelectTrigger data-testid="experience-select">
                      <SelectValue placeholder="Select experience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any Experience</SelectItem>
                      <SelectItem value="0-1 years">Fresher (0-1 years)</SelectItem>
                      <SelectItem value="1-3 years">1-3 years</SelectItem>
                      <SelectItem value="3-5 years">3-5 years</SelectItem>
                      <SelectItem value="5-10 years">5-10 years</SelectItem>
                      <SelectItem value="10+ years">10+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="qualifications">Qualifications Required</Label>
                  <Input
                    id="qualifications"
                    name="qualifications"
                    placeholder="e.g., B.Ed, M.Sc, PhD"
                    value={formData.qualifications}
                    onChange={handleChange}
                    data-testid="qualifications-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Compensation & Benefits */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5 text-green-600" />
                Compensation & Benefits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label htmlFor="salary">Salary / Compensation *</Label>
                <Input
                  id="salary"
                  name="salary"
                  placeholder="e.g., 30,000 - 50,000/month or 500/hour"
                  value={formData.salary}
                  onChange={handleChange}
                  required
                  data-testid="salary-input"
                />
                <p className="text-xs text-gray-500 mt-1">You can specify monthly salary or per-hour rate</p>
              </div>

              <div>
                <Label htmlFor="benefits">Benefits & Perks</Label>
                <Textarea
                  id="benefits"
                  name="benefits"
                  placeholder="e.g., Health insurance, transport allowance, free meals, professional development opportunities..."
                  value={formData.benefits}
                  onChange={handleChange}
                  rows={3}
                  data-testid="benefits-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-500" />
                Job Location
              </CardTitle>
              <CardDescription>Set the location where the tutor will work</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Autocomplete onLoad={setAutocomplete} onPlaceChanged={onPlaceChanged}>
                        <Input placeholder="Search for an address..." data-testid="location-search-input" />
                      </Autocomplete>
                    </div>
                    <Button type="button" variant="outline" onClick={getCurrentLocation}>
                      <MapPin className="h-4 w-4 mr-2" />
                      My Location
                    </Button>
                  </div>

                  <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13} onClick={onMapClick}>
                    {markerPosition && <Marker position={markerPosition} />}
                  </GoogleMap>

                  <div>
                    <Label htmlFor="location">Location Address *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Full address of the job location"
                      required
                      data-testid="location-address-input"
                    />
                  </div>

                  {formData.latitude && formData.longitude && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Coordinates: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                    </p>
                  )}
                </div>
              </LoadScript>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4 pb-8">
            <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 px-8" data-testid="submit-job-btn">
              {loading ? (editMode ? 'Updating...' : 'Posting...') : (editMode ? 'Update Job' : 'Post Job')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
